import type { ProspectMatterType, ProspectPracticeArea } from '@common/enums';
import type { RetainerAgreementWithFileDetails } from '@common/types/retainerAgreements';
import type { UserEntry } from '@common/types/users';
import { getMatterTypeLabel, getPracticeAreaLabel } from '@common/util';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormHelperText,
    Grid,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import AlertDialog from '@/components/AlertDialog';
import LoadingButton from '@/components/Buttons/LoadingButton';
import FormUploadField from '@/components/FormComponent/FormUploadField';
import { THIN_SCROLLBAR_STYLING } from '@/constants';
import { modifyRetainerAgreement } from '@/services/LegalDocumentService';
import { errorSnackbar, warningSnackbar } from '@/utils/snackbar';
import { ALL_USERS_LABEL, ALL_USERS_OPTION } from './constants';

const VALIDATION_SCHEMA = {
    fileCount: 1,
    fileSize: 10 * 1024 * 1024,
    fileType: [
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/pdf',
    ],
};

interface Props {
    selectedDocument: RetainerAgreementWithFileDetails;
    users: UserEntry[];
    lawFirmId: number;
    onClose(): void;
}

export default function EditRetainerModal({ selectedDocument, users, lawFirmId, onClose }: Props) {
    const queryClient = useQueryClient();

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
    const [isCloseAlert, setIsCloseAlert] = useState(false);

    const initialDocumentDisplayName = selectedDocument.documentDisplayName ?? '';

    // Form data — file and document display name are editable; the rest comes from selectedDocument
    const [fileIds, setFileIds] = useState<string[]>([]);
    const [documentDisplayName, setDocumentDisplayName] = useState(initialDocumentDisplayName);

    // Error data
    const [fileIdError, setFileIdError] = useState<string | null>(null);

    const handleFileIdsChange = (ids: string[]) => {
        setFileIds(ids);
        if (ids.length > 0) {
            setFileIdError(null);
        }
    };

    const handleSubmit = async () => {
        setHasAttemptedSubmit(true);

        const trimmedDisplayName = documentDisplayName.trim();
        const displayNameUnchanged =
            (trimmedDisplayName || null) === (selectedDocument.documentDisplayName ?? null);
        const hasNewFile = fileIds.length > 0;

        if (!hasNewFile && displayNameUnchanged) {
            warningSnackbar('No changes to save.', true);
            return;
        }

        try {
            setIsSubmitting(true);
            await modifyRetainerAgreement(
                {
                    practiceArea: selectedDocument.practiceArea as ProspectPracticeArea,
                    matterType: selectedDocument.matterType as ProspectMatterType,
                    userId: selectedDocument.userId,
                    province: selectedDocument.province,
                    fileId: hasNewFile ? fileIds[0] : selectedDocument.fileId,
                    documentDisplayName: trimmedDisplayName || null,
                },
                selectedDocument.retainerAgreementId,
            );
            await queryClient.invalidateQueries({ queryKey: ['retainerAgreements'] });
            onClose();
        } catch (error) {
            console.error('Error updating legal document:', error);
            errorSnackbar('Failed to update legal document.', true);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCloseModal = () => {
        const trimmedDisplayName = documentDisplayName.trim();
        const displayNameUnchanged =
            (trimmedDisplayName || null) === (selectedDocument.documentDisplayName ?? null);
        if (fileIds.length === 0 && displayNameUnchanged) {
            onClose();
        } else {
            setIsCloseAlert(true);
        }
    };

    return (
        <Dialog open maxWidth={false} PaperProps={{ sx: { width: '50vw' } }}>
            <DialogTitle>Upload New Legal Document</DialogTitle>
            <DialogContent sx={THIN_SCROLLBAR_STYLING}>
                <Grid container spacing={3} mt={1}>
                    <Grid item xs={12} lg={6}>
                        <Typography variant="labelLarge" component="p">
                            Select Practice Area
                        </Typography>
                        <TextField
                            name="practiceArea"
                            select
                            sx={{ width: '100%', mt: 1 }}
                            value={selectedDocument.practiceArea || ''}
                            label="Practice Area"
                            variant="outlined"
                            disabled
                        >
                            <MenuItem
                                key={selectedDocument.practiceArea}
                                value={selectedDocument.practiceArea}
                            >
                                {getPracticeAreaLabel(selectedDocument.practiceArea)}
                            </MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={12} lg={6}>
                        <Typography variant="labelLarge" component="p">
                            Available To
                        </Typography>
                        <TextField
                            name="userId"
                            select
                            sx={{ width: '100%', mt: 1 }}
                            value={selectedDocument.userId ?? ALL_USERS_OPTION}
                            label="Available To"
                            variant="outlined"
                            disabled
                        >
                            <MenuItem value={ALL_USERS_OPTION}>{ALL_USERS_LABEL}</MenuItem>
                            {users.map((user) => (
                                <MenuItem key={user?.userId} value={user?.userId}>
                                    {user?.fullName}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} lg={6}>
                        <Typography variant="labelLarge" component="p">
                            Select Matter Type
                        </Typography>
                        <TextField
                            name="matterType"
                            select
                            sx={{ width: '100%', mt: 1 }}
                            value={selectedDocument.matterType || ''}
                            label="Matter Type"
                            variant="outlined"
                            disabled
                        >
                            <MenuItem
                                key={selectedDocument.matterType}
                                value={selectedDocument.matterType as string}
                            >
                                {getMatterTypeLabel(selectedDocument.matterType)}
                            </MenuItem>
                        </TextField>
                    </Grid>
                </Grid>
                <Grid container spacing={3} mt={1}>
                    <Grid item xs={12} lg={6}>
                        <Typography variant="labelLarge" component="p">
                            PDF download name (optional)
                        </Typography>
                        <TextField
                            name="documentDisplayName"
                            sx={{ width: '100%', mt: 1 }}
                            value={documentDisplayName}
                            onChange={(event) => {
                                setDocumentDisplayName(event.target.value);
                            }}
                            label="Custom name for PDF downloads"
                            variant="outlined"
                            helperText="Leave blank for default names (Legal Document / Signed Legal Document). When set, used as the prefix for unsigned and signed PDF downloads."
                        />
                    </Grid>
                </Grid>
                <Grid container spacing={3} mt={1}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="labelLarge" component="p">
                            Upload Legal Document
                        </Typography>
                        <FormUploadField
                            typeofDocument="Retainer Agreement"
                            name="retainer-agreement-file"
                            fileIds={fileIds}
                            selectedLawFirmId={lawFirmId}
                            onFileIdsChange={handleFileIdsChange}
                            deleteOnRemove
                            validationSchema={VALIDATION_SCHEMA}
                            isError={hasAttemptedSubmit && !!fileIdError}
                        />
                        {hasAttemptedSubmit && fileIdError && (
                            <FormHelperText error>{fileIdError}</FormHelperText>
                        )}
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Stack direction="row" spacing={1}>
                    <Button variant="outlined" onClick={handleCloseModal}>
                        Cancel
                    </Button>
                    <LoadingButton
                        loading={isSubmitting}
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                    >
                        Submit
                    </LoadingButton>
                </Stack>
            </DialogActions>
            <AlertDialog
                open={isCloseAlert}
                handleClose={() => setIsCloseAlert(false)}
                handleConfirm={onClose}
                title="Are you sure?"
                message="Closing the popup will result in loss of any data that you have filled up. Are you sure you want to close the popup?"
            />
        </Dialog>
    );
}
